export const QUESTION_STATE = {
  current: 'Current',
  future: 'Future',
} as const;

export type SubCapabilityQuestionStateKind = 'Current' | 'Future';

export type SubCapabilityQuestionDraft = {
  id?: number;
  capabilityId?: number;
  subCapabilityId?: number;
  questionText: string;
  stateKind: SubCapabilityQuestionStateKind;
  displayOrder?: number;
  isActive?: boolean;
};

export const createEmptyQuestion = (
  stateKind: SubCapabilityQuestionStateKind,
  displayOrder = 0
): SubCapabilityQuestionDraft => ({
  id: 0,
  questionText: '',
  stateKind,
  displayOrder,
  isActive: true,
});

export const joinQuestionTexts = (questions?: SubCapabilityQuestionDraft[] | null): string =>
  (questions || [])
    .map((question) => String(question?.questionText || '').trim())
    .filter(Boolean)
    .join('\n\n');

export const normalizeQuestionList = (
  list: unknown,
  stateKind: SubCapabilityQuestionStateKind
): SubCapabilityQuestionDraft[] => {
  if (!Array.isArray(list)) return [];

  return list
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const raw = item as Record<string, unknown>;
      const questionText = String(raw.questionText ?? raw.QuestionText ?? '').trim();
      if (!questionText) return null;
      return {
        id: Number(raw.id ?? raw.Id ?? 0) || 0,
        capabilityId: Number(raw.capabilityId ?? raw.CapabilityId ?? 0) || 0,
        subCapabilityId: Number(raw.subCapabilityId ?? raw.SubCapabilityId ?? 0) || 0,
        questionText,
        stateKind,
        displayOrder: Number(raw.displayOrder ?? raw.DisplayOrder ?? index) || index,
        isActive: raw.isActive ?? raw.IsActive ?? true,
      } as SubCapabilityQuestionDraft;
    })
    .filter(Boolean) as SubCapabilityQuestionDraft[];
};

export const withQuestionList = (
  subCap: Record<string, unknown>,
  stateKind: SubCapabilityQuestionStateKind,
  questions: SubCapabilityQuestionDraft[]
) => {
  const next = questions.map((question, index) => ({
    ...question,
    stateKind,
    displayOrder: index,
  }));

  if (stateKind === QUESTION_STATE.current) {
    return { ...subCap, currentStateQuestions: next };
  }

  return { ...subCap, futureStateQuestions: next };
};

export const mapQuestionsForSave = (questions?: SubCapabilityQuestionDraft[] | null) =>
  (questions || [])
    .map((question, index) => ({
      id: question.id || 0,
      capabilityId: question.capabilityId || 0,
      subCapabilityId: question.subCapabilityId || 0,
      questionText: String(question.questionText || '').trim(),
      stateKind: question.stateKind,
      displayOrder: index,
      isActive: question.isActive !== false,
    }))
    .filter((question) => question.questionText.length > 0);
